import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react';

const CONTENT_ROUTE_SEGMENT: Record<string, string> = {
	page: 'pages',
	whiteboard: 'whiteboard',
	database: 'database',
};

export const getLongContentLink = ({
	contentId,
	contentType,
	spaceKey,
	origin,
	pathname,
}: {
	contentId: string;
	contentType: string;
	spaceKey: string;
	origin: string;
	pathname: string;
}): string | undefined => {
	const routeSegment = CONTENT_ROUTE_SEGMENT[contentType];
	if (!contentId || !spaceKey || !routeSegment) {
		return undefined;
	}

	const contextPath = pathname.match(/^(.*)\/spaces\//)?.[1] ?? '/wiki';
	return `${origin}${contextPath}/spaces/${encodeURIComponent(spaceKey)}/${routeSegment}/${encodeURIComponent(contentId)}`;
};

type ClipboardWriter = {
	writeText: (value: string) => Promise<void>;
};

export const copyToClipboard = (
	value: string | undefined,
	clipboard: ClipboardWriter | undefined,
): void => {
	if (!value || !clipboard || typeof clipboard.writeText !== 'function') {
		return;
	}
	void clipboard.writeText(value).catch(() => undefined);
};

export type FlagToShareActions = {
	contentId: string;
	openShareDialog: () => void;
	copyShareLink: () => void;
};

export type FlagToShareActionRegistration = {
	contentId: string;
	contentType: string;
	spaceKey: string;
	isCopyShareLinkDisabled: boolean;
	openShareDialog: () => void;
};

export type RegisterFlagToShareActions = (actions: FlagToShareActions) => () => void;

type Registration = {
	token: object;
	actions: FlagToShareActions;
};

type FlagToShareActionsContextValue = {
	current: Registration | null;
	register: RegisterFlagToShareActions;
};

const FlagToShareActionsContext = createContext<FlagToShareActionsContextValue | undefined>(
	undefined,
);

const useFlagToShareActionsContext = (): FlagToShareActionsContextValue => {
	const context = useContext(FlagToShareActionsContext);
	if (!context) {
		throw new Error('Flag-to-Share action hooks must be used within FlagToShareActionsProvider');
	}
	return context;
};

export const FlagToShareActionsProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	const [registration, setRegistration] = useState<Registration | null>(null);

	const register = useCallback<RegisterFlagToShareActions>((actions) => {
		const token = {};
		setRegistration({ token, actions });

		return () => {
			setRegistration((current) => (current?.token === token ? null : current));
		};
	}, []);

	const value = useMemo(() => ({ current: registration, register }), [registration, register]);

	return (
		<FlagToShareActionsContext.Provider value={value}>
			{children}
		</FlagToShareActionsContext.Provider>
	);
};

export const useRegisterFlagToShareActions = (): RegisterFlagToShareActions =>
	useFlagToShareActionsContext().register;

export const useFlagToShareActionRegistration = ({
	contentId,
	contentType,
	spaceKey,
	isCopyShareLinkDisabled,
	openShareDialog,
}: FlagToShareActionRegistration): void => {
	const register = useRegisterFlagToShareActions();
	const actions = useMemo<FlagToShareActions>(
		() => ({
			contentId,
			openShareDialog,
			copyShareLink: () => {
				if (isCopyShareLinkDisabled) {
					return;
				}
				const longContentLink = getLongContentLink({
					contentId,
					contentType,
					spaceKey,
					origin: window.location.origin,
					pathname: window.location.pathname,
				});
				const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
				copyToClipboard(longContentLink, clipboard);
			},
		}),
		[contentId, contentType, isCopyShareLinkDisabled, openShareDialog, spaceKey],
	);
	const actionsRef = useRef(actions);

	useEffect(() => {
		actionsRef.current = actions;
	}, [actions]);

	useEffect(
		() =>
			register({
				contentId,
				openShareDialog: () => actionsRef.current.openShareDialog(),
				copyShareLink: () => actionsRef.current.copyShareLink(),
			}),
		[contentId, register],
	);
};

export const useCurrentFlagToShareActions = (): FlagToShareActions | undefined =>
	useFlagToShareActionsContext().current?.actions;
